import { Dispatch, SetStateAction, useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { Grid, Typography, Button, Alert, Chip } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import GridOnIcon from "@mui/icons-material/GridOn";
import QueueIcon from "@mui/icons-material/Queue";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from "react-i18next";
import { useShopIndex } from "queries/shops";
import AddShopModal from "./AddShopModal";
import InventoryProposalModal from "./InventoryProposalModal";
import { Api } from "queries/types";
import { Feature, useAuthorization } from "user";
import Filters from "./Filters";
import useFilters, { filteredShop } from "./useFilters";
import CardWithFooter, { CardFooterButtons } from "components/CardWithFooter";
import { humanDate } from "utils/date";
import { download } from "queries/http";

const cellsCompletionColor = (recorded: number, recordable: number) => {
  if (recorded === 0) return "#e00e1d";
  return recorded === recordable ? "#0b6b3a" : "#e00e1d";
};

interface CardFooterButtonsForShopProps {
  shop: Api.Shop;
  hasAccess: (feature: Feature) => boolean;
  t: (key:string) => string;
  navigate: NavigateFunction;
  setInventoryProposalShop: Dispatch<SetStateAction<Api.Shop | undefined>>;
}

const getCardFooterButtonsForShop = ({ shop, hasAccess, t, navigate, setInventoryProposalShop }: CardFooterButtonsForShopProps) => {
  const inventoryAvailable = shop.shopGrids.filter((grid) => grid.isActive).length > 0;
  const hasRecordedCellsCount = inventoryAvailable && shop.recordableCellsCount >= 1;
  const recordedCellsAvailable = hasRecordedCellsCount && shop.recordedCellsCount >= 1;
  const hasHistory = inventoryAvailable && shop.shopGrids && shop.shopGrids.length > 1;
  const arrCrdFtrBtnProps = [];

  if (hasAccess(Feature.ShopEdit)) {
    arrCrdFtrBtnProps.push({
      Icon: SettingsIcon,
      text: t("shop.index.edit"),
      onClick: () => navigate(`/shops/${shop.erpId}/edit`)
    });
  }

  if (hasAccess(Feature.ShopPropose) && recordedCellsAvailable) {
    arrCrdFtrBtnProps.push({
      Icon: QueueIcon,
      text: t("shop.index.propose"),
      onClick: () => setInventoryProposalShop(shop)
    });
  }

  if (hasRecordedCellsCount) {
    arrCrdFtrBtnProps.push({
      Icon: GridOnIcon,
      text: t("shop.index.inventory"),
      onClick: () => navigate(`/shops/${shop.erpId}/inventory`),
    });
  }

  if (hasAccess(Feature.ShopComparator) && hasHistory) {
    arrCrdFtrBtnProps.push({
      Icon: HistoryIcon,
      text: t("shop.index.compare"),
      onClick: () => navigate(`/shops/${shop.erpId}/compare`)
    });
  }

  if (hasAccess(Feature.Pdf) && recordedCellsAvailable) {
    arrCrdFtrBtnProps.push({
      Icon: FileDownloadIcon,
      text: t("shop.index.pdf"),
      onClick: () => download(`/api/shops/${shop.erpId}/map/pdf/`),
    });
  }

  return arrCrdFtrBtnProps;
};

const onClickCardBody = (shop: Api.Shop, hasAccess: (feature: Feature) => boolean, navigate: NavigateFunction) => {
  if (hasAccess(Feature.ShopEdit)) {
    navigate(`/shops/${shop.erpId}/edit`);
  }
};

const ShopIndexPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filters, setFilters, params: filtersParams, searchTerm, completionRate } = useFilters();
  const { data: shops = [], isError } = useShopIndex();
  const [addShopModalOpen, setAddShopModalOpen] = useState(false);
  const [inventoryProposalShop, setInventoryProposalShop] = useState<Api.Shop>();
  const hasAccess = useAuthorization();
  if (isError) return <Alert severity="error">{t("error")}</Alert>;

  const shopsFiltered = filteredShop(shops, filtersParams, searchTerm, completionRate);

  return (
    <div>
      {hasAccess(Feature.ShopFilters) && <Filters filters={filters} setFilters={setFilters} />}
      <Typography component="h1" variant="h6" sx={{ mt: 4 }}>
        {t("shop.index.shops", { count: shopsFiltered ? shopsFiltered.length : shops.length })}
        {hasAccess(Feature.ShopAdd) && (
          <Button variant="contained" onClick={() => setAddShopModalOpen(true)} sx={{ ml: 2, fontWeight: 500 }}>
            {t("shop.index.add")}
          </Button>
        )}
      </Typography>
      <Grid container spacing={3} mt={-1} pb={2}>
        {shopsFiltered.map((shop) => {
          const inventoryAvailable = shop.shopGrids.filter((grid) => grid.isActive).length > 0;
          const arrCrdFtrBtnProps = getCardFooterButtonsForShop({ shop, hasAccess, t, navigate, setInventoryProposalShop });

          return (
            <Grid key={shop.erpId} item sm={4} xs={12}>
              <CardWithFooter
                body={
                  <Grid container height="100%" justifyContent="space-between" sx={{ cursor: hasAccess(Feature.ShopEdit) ? 'pointer' : 'default' }} onClick={() => onClickCardBody(shop, hasAccess, navigate)}>
                    <Grid item xs={7}>
                      <Typography variant="h6" fontWeight={700} fontSize="16px">
                        {shop.name}
                      </Typography>
                      <Typography fontWeight={500} fontSize={10}>
                        {`${shop.brandId}-${shop.erpId}`}
                      </Typography>
                    </Grid>
                    {inventoryAvailable && (
                      <Grid item xs={4}>
                        <Grid container direction="column" sx={{ height: "100%" }} justifyContent="space-between">
                          <Grid item sx={{ textAlign: "center" }}>
                            <Chip label={humanDate(shop.activeGridUpdatedAt)} sx={{ borderRadius: "6px" }} />
                            <Typography fontWeight={500} fontSize={10}>
                              {t("shop.index.lastUpdate")}
                            </Typography>
                          </Grid>
                          <Grid item sx={{ textAlign: "center" }}>
                            <Typography fontSize={14} lineHeight="24px">
                              <span
                                style={{
                                  fontWeight: 400,
                                  color: cellsCompletionColor(shop.recordedCellsCount, shop.recordableCellsCount),
                                }}
                              >
                                {shop.recordedCellsCount}
                              </span>{" "}
                              / <span style={{ fontWeight: 600 }}>{shop.recordableCellsCount}</span>
                            </Typography>
                            <Typography fontWeight={500} fontSize={10}>
                              {t("shop.index.recordedCells")}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                }
                actions={
                  <CardFooterButtons arrCrdFtrBtnProps={arrCrdFtrBtnProps} label={t("shop.index.moreAction")}/>
                }
              />
            </Grid>
          );
        })}
      </Grid>

      {addShopModalOpen && <AddShopModal onClose={() => setAddShopModalOpen(false)} />}
      {inventoryProposalShop && (
        <InventoryProposalModal shop={inventoryProposalShop} onClose={(gridId?:number) => {
          inventoryProposalShop.shopGrids = inventoryProposalShop.shopGrids.filter(shopGrid => shopGrid.id !== gridId);
          setInventoryProposalShop(undefined)
        }} />
      )}
    </div>
  );
};

export default ShopIndexPage;
